import { motion } from 'framer-motion';
import { Bot, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { useSiteConfig } from '../../hooks/useSiteConfig';
import { openWhatsApp } from '../../utils/whatsapp';

export default function AIFeatureSection() {
  const siteConfig = useSiteConfig();
  const { aiFeature, contact } = siteConfig;
  const [ctaLabel, setCtaLabel] = useState(aiFeature.ctaText);

  const handleCta = () => {
    setCtaLabel('⏳ 正在開啟 WhatsApp...');
    openWhatsApp(contact.whatsapp, aiFeature.whatsappMessage);
  };

  return (
    <section id="ai-feature" className="py-20 md:py-28 bg-deep-navy relative overflow-hidden">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-neon-gold/10 rounded-full blur-3xl" />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-neon-gold/30 bg-neon-gold/5 text-neon-gold text-xs md:text-sm font-medium mb-6">
            <Bot size={16} />
            AI 數字人技術
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
            {aiFeature.sectionTitle}
          </h2>
          <p className="text-base md:text-lg text-gray-400">{aiFeature.subtitle}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="neon-card rounded-3xl p-8 md:p-12 text-center"
        >
          <p className="text-gray-300 text-base md:text-lg leading-relaxed mb-8 max-w-2xl mx-auto">
            {aiFeature.description}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8 max-w-3xl mx-auto">
            {aiFeature.fears.map((fear, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                className="px-4 py-4 rounded-xl bg-white/5 border border-neon-gold/20 text-white font-medium text-sm md:text-base"
              >
                {fear}
              </motion.div>
            ))}
          </div>

          <div className="flex flex-wrap items-center justify-center gap-2 mb-10 max-w-2xl mx-auto">
            {aiFeature.capabilities.map((cap, i) => (
              <span
                key={i}
                className="px-3 py-1.5 rounded-full text-xs md:text-sm font-medium bg-neon-gold/10 border border-neon-gold/30 text-neon-gold"
              >
                {cap}
              </span>
            ))}
          </div>

          <div className="flex items-center justify-center gap-3 mb-8">
            <div className="h-px flex-1 max-w-16 bg-gradient-to-r from-transparent to-neon-gold/50" />
            <Sparkles size={20} className="text-neon-gold" />
            <span className="text-xl md:text-2xl font-bold" style={{ color: '#D4A853', textShadow: '0 0 12px rgba(212,168,83,0.6)' }}>
              {aiFeature.solution}
            </span>
            <Sparkles size={20} className="text-neon-gold" />
            <div className="h-px flex-1 max-w-16 bg-gradient-to-l from-transparent to-neon-gold/50" />
          </div>

          <button
            onClick={handleCta}
            className="inline-block px-8 py-4 bg-gradient-to-r from-neon-gold to-neon-purple text-deep-navy font-bold rounded-full transition-all duration-300 hover:brightness-110 hover:-translate-y-0.5"
          >
            {ctaLabel}
          </button>
        </motion.div>
      </div>
    </section>
  );
}
